import React, { useMemo } from 'react';
import type { ActivityViewMode } from '../../core/types/activityViewMode.types';
import type {
  InflationMarketPayload,
  SandboxPricePoint,
} from '../../core/types/payloads/sandbox.types';
import type { InflationMarketRuntime } from '../../core/types/payloads/sandbox.types';
import { buildInflationPriceSeries, formatExploraCurrency } from './exploraChartUtils';

type InflationProjection = {
  currentPrice: number;
  targetPrice: number;
  productName: string;
  history: SandboxPricePoint[];
  timeRemainingSec?: number;
};

export interface InflationLiveStageProps {
  payload: InflationMarketPayload;
  runtime: InflationMarketRuntime;
  projection?: InflationProjection;
  mode?: ActivityViewMode;
  fill?: boolean;
  teamId?: string;
  dispatch?: (action: { type: string; payload?: unknown }) => void;
  onPayloadChange?: (patch: Partial<InflationMarketPayload>) => void;
  children?: React.ReactNode;
}

function InflationChart({
  payload,
  runtime,
  projection,
}: {
  payload: InflationMarketPayload;
  runtime: InflationMarketRuntime;
  projection?: InflationProjection;
}) {
  const sym = payload.currencySymbol ?? '$';
  const currentPrice = projection?.currentPrice ?? runtime.currentPrice;
  const history = projection?.history ?? runtime.history;
  const series = useMemo(
    () => buildInflationPriceSeries(payload.basePrice, history),
    [payload.basePrice, history],
  );

  const values = series.map((p) => p.value);
  const minY = Math.min(payload.basePrice, payload.targetPrice, ...values) * 0.95;
  const maxY = Math.max(payload.basePrice, payload.targetPrice, ...values) * 1.05;
  const rangeY = maxY - minY || 1;
  const VB = { w: 520, h: 240 };
  const pad = { l: 56, r: 20, t: 16, b: 40 };
  const plotW = VB.w - pad.l - pad.r;
  const plotH = VB.h - pad.t - pad.b;
  const toX = (i: number) =>
    pad.l + (series.length <= 1 ? plotW / 2 : (i / (series.length - 1)) * plotW);
  const toY = (v: number) => pad.t + plotH - ((v - minY) / rangeY) * plotH;
  const linePath = series
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(p.value).toFixed(1)}`)
    .join(' ');
  const exact = currentPrice === payload.targetPrice;

  return (
    <div className="explora-inflation-chart">
      <div className="explora-inflation-chart__price">
        <span className="explora-inflation-chart__label">{payload.productName}</span>
        <span className="explora-inflation-chart__amount" data-exact={exact}>
          {formatExploraCurrency(currentPrice, undefined, sym)}
        </span>
        <span className="explora-inflation-chart__target">
          Meta {formatExploraCurrency(payload.targetPrice, undefined, sym)}
        </span>
      </div>
      <svg viewBox={`0 0 ${VB.w} ${VB.h}`} className="explora-inflation-chart__svg" aria-hidden>
        <line
          x1={pad.l}
          y1={toY(payload.targetPrice)}
          x2={VB.w - pad.r}
          y2={toY(payload.targetPrice)}
          stroke="#f87171"
          strokeWidth={2}
          strokeDasharray="5 4"
        />
        {series.length > 1 && (
          <path d={linePath} fill="none" stroke="#38bdf8" strokeWidth={2.5} strokeLinejoin="round" />
        )}
        {series.map((p, i) => (
          <circle key={i} cx={toX(i)} cy={toY(p.value)} r={4} fill="#38bdf8" />
        ))}
      </svg>
    </div>
  );
}

function TeamsPanel({
  payload,
  mode,
  dispatch,
  teamId,
  onPayloadChange,
}: {
  payload: InflationMarketPayload;
  mode: ActivityViewMode;
  dispatch?: InflationLiveStageProps['dispatch'];
  teamId?: string;
  onPayloadChange?: InflationLiveStageProps['onPayloadChange'];
}) {
  const isEditor = mode === 'editor';
  const isStudent = mode === 'student' && teamId;

  if (isStudent) {
    const team = payload.teams.find((t) => t.teamId === teamId);
    if (!team) return null;
    return (
      <div className="explora-inflation-teams">
        <p className="explora-inflation-teams__title">{team.label}</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {team.modifiers.map((mod) => (
            <button
              key={mod.id}
              type="button"
              className="explora-action-btn"
              onClick={() =>
                dispatch?.({ type: 'APPLY_MODIFIER', payload: { modifierId: mod.id, teamId } })
              }
            >
              {mod.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <aside className="explora-inflation-sidebar">
      <p className="explora-sidebar-title">Equipos</p>
      {payload.teams.map((team, ti) => (
        <div key={team.teamId} className="explora-inflation-team">
          {isEditor ? (
            <input
              className="canvas-inline-input w-full bg-transparent text-xs font-bold"
              value={team.label}
              onChange={(e) => {
                const teams = payload.teams.map((t, i) =>
                  i === ti ? { ...t, label: e.target.value } : t,
                );
                onPayloadChange?.({ teams });
              }}
            />
          ) : (
            <p className="text-xs font-bold">{team.label}</p>
          )}
          <ul className="explora-inflation-mod-list">
            {team.modifiers.map((mod, mi) => (
              <li key={mod.id} className="explora-inflation-mod">
                {isEditor ? (
                  <>
                    <input
                      className="canvas-inline-input flex-1 bg-transparent text-[10px]"
                      value={mod.label}
                      onChange={(e) => {
                        const teams = payload.teams.map((t, i) => {
                          if (i !== ti) return t;
                          return {
                            ...t,
                            modifiers: t.modifiers.map((m, j) =>
                              j === mi ? { ...m, label: e.target.value } : m,
                            ),
                          };
                        });
                        onPayloadChange?.({ teams });
                      }}
                    />
                    <input
                      type="number"
                      className="w-12 bg-transparent text-right font-mono text-[10px]"
                      value={mod.percent}
                      onChange={(e) => {
                        const teams = payload.teams.map((t, i) => {
                          if (i !== ti) return t;
                          return {
                            ...t,
                            modifiers: t.modifiers.map((m, j) =>
                              j === mi ? { ...m, percent: Number(e.target.value) } : m,
                            ),
                          };
                        });
                        onPayloadChange?.({ teams });
                      }}
                    />
                  </>
                ) : (
                  <span>{mod.label}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </aside>
  );
}

export function InflationLiveStage({
  payload,
  runtime,
  projection,
  mode = 'projector',
  fill = false,
  teamId,
  dispatch,
  onPayloadChange,
  children,
}: InflationLiveStageProps) {
  const isEditor = mode === 'editor';
  const roundActive = Boolean(runtime.startedAt && runtime.deadlineAt);
  const timeLeft = projection?.timeRemainingSec;

  return (
    <div className={`explora-live-stage explora-live-stage--inflation ${fill ? 'explora-live-stage--fill' : ''}`} data-mode={mode}>
      <header className="explora-toolbar">
        <div className="explora-toolbar-start min-w-0 flex-1">
          {isEditor ? (
            <input
              className="canvas-inline-input canvas-inline-input--title min-w-0 flex-1 bg-transparent font-display text-base font-bold"
              value={payload.title}
              onChange={(e) => onPayloadChange?.({ title: e.target.value })}
            />
          ) : (
            <>
              <span className="explora-chip">Mercado</span>
              {roundActive ? (
                <span className="explora-chip explora-chip--success">Ronda activa</span>
              ) : (
                <span className="explora-chip explora-chip--muted">En espera</span>
              )}
              <h3 className="min-w-0 truncate font-display text-sm font-bold">{payload.title}</h3>
            </>
          )}
        </div>
        <div className="explora-toolbar-end">
          {!isEditor && timeLeft != null && (
            <span className="explora-chip">{timeLeft}s</span>
          )}
        </div>
      </header>

      {isEditor && (
        <div className="explora-subtoolbar grid grid-cols-2 gap-2 sm:grid-cols-4">
          <input
            className="canvas-inline-input bg-transparent text-xs"
            value={payload.productName}
            placeholder="Producto"
            onChange={(e) => onPayloadChange?.({ productName: e.target.value })}
          />
          <input
            type="number"
            className="canvas-inline-input bg-transparent text-xs"
            value={payload.basePrice}
            placeholder="Precio base"
            onChange={(e) => onPayloadChange?.({ basePrice: Number(e.target.value) })}
          />
          <input
            type="number"
            className="canvas-inline-input bg-transparent text-xs"
            value={payload.targetPrice}
            placeholder="Precio meta"
            onChange={(e) => onPayloadChange?.({ targetPrice: Number(e.target.value) })}
          />
          <input
            type="number"
            className="canvas-inline-input bg-transparent text-xs"
            value={payload.roundDurationSec}
            placeholder="Duración (s)"
            onChange={(e) => onPayloadChange?.({ roundDurationSec: Number(e.target.value) })}
          />
        </div>
      )}

      <div className="explora-body">
        <div className="explora-canvas-wrap">
          <div className="explora-canvas-main">
            <InflationChart payload={payload} runtime={runtime} projection={projection} />
          </div>
          {children && <div className="explora-context-bar">{children}</div>}
          {mode === 'student' && teamId && (
            <div className="explora-student-bar">
              <TeamsPanel payload={payload} mode={mode} dispatch={dispatch} teamId={teamId} />
            </div>
          )}
        </div>

        {mode !== 'student' && !sidebarCollapsed && (
          <TeamsPanel
            payload={payload}
            mode={mode}
            dispatch={dispatch}
            onPayloadChange={onPayloadChange}
          />
        )}
      </div>
    </div>
  );
}

export function InflationShell(
  props: Omit<InflationLiveStageProps, 'mode' | 'fill'> & { children?: React.ReactNode },
) {
  return <InflationLiveStage {...props} mode="host" fill />;
}
